import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { dt, inr } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ProviderPayments() {
  const [rows, setRows] = useState(null);
  const [balances, setBalances] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ provider: "", amount: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/finance/provider-payments").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/finance/provider-balances").then((r) => setBalances(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/finance/provider-payments", { provider: form.provider, amount: parseFloat(form.amount), note: form.note });
      toast.success("Payment recorded");
      setOpen(false); setForm({ provider: "", amount: "", note: "" });
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Bank provider payments</h2>
          <p className="text-sm text-muted-foreground">Record settlements paid out to each bank provider.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-provider-payment-button"><Plus className="mr-1.5 h-4 w-4" /> Record payment</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Record a provider payment</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5"><Label>Bank provider</Label>
                <Input required list="prov-list" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="KKBROTHERS" data-testid="pp-provider-input" />
                <datalist id="prov-list">{balances.map((b) => <option key={b.provider} value={b.provider} />)}</datalist>
              </div>
              <div className="space-y-1.5"><Label>Amount (₹)</Label><Input type="number" min="1" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pp-amount-input" /></div>
              <div className="space-y-1.5"><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional" data-testid="pp-note-input" /></div>
              <DialogFooter><Button type="submit" disabled={saving} data-testid="pp-submit-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Bank provider</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {rows && rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No payments recorded.</TableCell></TableRow>}
              {rows && rows.map((r) => (
                <TableRow key={r.id} data-testid={`provider-payment-row-${r.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dt(r.created_at)}</TableCell>
                  <TableCell className="font-semibold">{r.provider}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{inr(r.amount)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">{r.note || "—"}</TableCell>
                  <TableCell className="text-sm">{r.created_by_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
